import { observable, action, computed } from 'mobx'
import i18n from 'i18next'
import orgs from 'store/orgs.store'
import modulesStore from './modulesStore.store'
import { ModuleEnum, ProviderEnum  } from 'utils/types'
import {
  testKehilaConnection,
  getKehilaCategories,
  createKehilaCategories,
  startKehilaImport,
  retryKehilaImport,
  getKehilaImportStatus,
  downloadKehilaLog,
  KehilaCategoryResponse,
  KehilaCreateCategoriesResponse,
  KehilaImportStatusResponse,
} from 'pages/Home/Protocols/KehilaMigration/kehilaMigrationApi'

// One-time Kehila -> Protocols migration wizard state. Kept fully separate from the rest of the
// app on purpose (own store, own api file, own components) - this whole feature is temporary and
// will be removed once the migration is complete.

export type KehilaCategoryRow = KehilaCategoryResponse & { skip: boolean }

const POLL_INTERVAL_MS = 5000

class KehilaMigrationWizardStore {
  @observable isOpen = false
  @observable step = 0
  @observable communityId = ''
  @observable probed = false
  @observable connectionCounts: { categoriesCount: number; protocolsCount: number } | null = null
  @observable categories: KehilaCategoryRow[] = []
  @observable loadingCategories = false
  @observable creatingCategories = false
  @observable createResult: KehilaCreateCategoriesResponse | null = null
  @observable starting = false
  @observable importStatus: KehilaImportStatusResponse | null = null
  @observable connectionError: string | null = null

  private baseUrl = ''
  private authToken = ''
  private pollTimer: ReturnType<typeof setInterval> | null = null

  @action open = () => {
    this.isOpen = true
    this.step = 0
    this.communityId = ''
    this.probed = false
    this.connectionCounts = null
    this.categories = []
    this.createResult = null
    this.importStatus = null
    this.connectionError = null
    this.baseUrl = ''
    this.authToken = ''
  }

  @action close = () => {
    this.stopPolling()
    this.isOpen = false
  }

  @action setCommunityId = (value: string) => {
    this.communityId = value.replace(/\D/g, '')
    this.probed = false
    this.connectionCounts = null
    this.connectionError = null
  }

  // Reuses the same Mekome -> Protocols token exchange the Protocols module itself uses
  // (see store/protocols.store.ts) to get both the auth token and the base URL - the base URL is
  // the installed module's own external_url field, exactly like the real Protocols tab does it,
  // rather than guessing it from the build environment.
  private ensureToken = async (): Promise<boolean> => {
    if (this.baseUrl && this.authToken) return true

    // orgs.currentOrgId is only set once the operator is logged in and has a community selected
    // (the top nav dropdown). Refuse to proceed rather than falling back to a fake community id.
    if (!orgs.currentOrgId) {
      this.connectionError = i18n.t('kehilaMigration:err_noCommunitySelected')
      console.error('[KehilaMigration] No community selected (orgs.currentOrgId is not set)')
      return false
    }

   //To work with the migration wizard, admin must crete the module for Protocols under selected
   //community in the drop-down. In this case this admin can get the P-JWT from Protocols and do the
   // migration process.
    const protocolsModule = modulesStore.communityModules.find(m => m.provider === ProviderEnum.PROTOCOLS)
    if (!protocolsModule) {
      this.connectionError = i18n.t('kehilaMigration:err_noProtocolsModule', { orgId: orgs.currentOrgId })
      console.error(
        '[KehilaMigration] No Protocols module found in modulesStore.communityModules for org',
        orgs.currentOrgId,
        modulesStore.communityModules.slice(),
      )
      return false
    }

    const token = await modulesStore.openProtocolModule(protocolsModule.module_id, orgs.currentOrgId)
    if (!token) {
      this.connectionError = i18n.t('kehilaMigration:err_tokenExchange')
      console.error('[KehilaMigration] openProtocolModule returned no token for module_id', protocolsModule.module_id)
      return false
    }

    this.baseUrl = typeof protocolsModule.external_url === 'string' ? protocolsModule.external_url.trim() : ''
    this.authToken = token
    return true
  }

  //checks if connection to Kehila is working, returns categories and protocols count.
  @action testConnection = async () => {
    if (!this.communityId) return
    this.connectionError = null
    const ready = await this.ensureToken()
    if (!ready) return
    const result = await testKehilaConnection(this.baseUrl, this.authToken, Number(this.communityId))
    if (result.success) {
      this.connectionCounts = result.data
      this.probed = true
    } else {
      this.connectionError = result.message
    }
  }

  //for displaying categories
  @action loadCategories = async () => {
    this.loadingCategories = true
    const res = await getKehilaCategories(this.baseUrl, this.authToken, Number(this.communityId))
    this.categories = res.map(c => ({ ...c, skip: false }))
    this.loadingCategories = false
  }

  //take/skip category
  @action toggleSkip = (oldCategoryId: number) => {
    const row = this.categories.find(c => c.oldCategoryId === oldCategoryId)
    if (row) {
      row.skip = !row.skip
      this.createResult = null
    }
  }

  @computed get liveCategories(): KehilaCategoryRow[] {
    return this.categories.filter(c => !c.skip)
  }

  // The destination community: migrated data is written into whichever Mekome community the
  // operator currently has selected in the top nav dropdown - independent of the Kehila source
  // community id typed into this wizard.
  // Safe to assert: ensureToken() refuses to proceed (and every Kehila API call goes through it
  // first) unless orgs.currentOrgId is already set.
  @computed get mekomeCommunityId(): number {
    return orgs.currentOrgId as number
  }

  //migrate Kehila categories - writes into Mekome only, never talks to Kehila.
  @action createCategories = async () => {
    this.creatingCategories = true
    const selection = this.liveCategories.map(c => ({ oldCategoryId: c.oldCategoryId, categoryName: c.categoryName }))
    const res = await createKehilaCategories(this.baseUrl, this.authToken, Number(this.communityId), this.mekomeCommunityId, selection)
    this.createResult = res
    this.creatingCategories = false
  }

  //import protocols
  @action startImport = async () => {
    this.starting = true

    const status = await getKehilaImportStatus(this.baseUrl, this.authToken, Number(this.communityId), this.mekomeCommunityId)
    if (status && !status.isRunning && status.total > 0 && status.done >= status.total) {
      // Everything is already imported (e.g. resumed after a full run) - nothing to start.
      this.importStatus = status
      this.starting = false
      return
    }

    await startKehilaImport(this.baseUrl, this.authToken, Number(this.communityId), this.mekomeCommunityId)
    this.starting = false
    await this.fetchStatus()
    this.startPolling()
  }

  //retry to migrate failed protocols
  @action retryFailed = async () => {
    this.starting = true
    await retryKehilaImport(this.baseUrl, this.authToken, Number(this.communityId), this.mekomeCommunityId)
    this.starting = false
    await this.fetchStatus()
    this.startPolling()
  }

  //for progressbar
  @action fetchStatus = async () => {
    const res = await getKehilaImportStatus(this.baseUrl, this.authToken, Number(this.communityId), this.mekomeCommunityId)
    if (res) {
      this.importStatus = res
      if (!res.isRunning && res.done >= res.total) {
        this.stopPolling()
      }
    }
  }

  startPolling = () => {
    if (this.pollTimer) return
    this.pollTimer = setInterval(this.fetchStatus, POLL_INTERVAL_MS)
  }

  stopPolling = () => {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }

  downloadLog = () => {
    downloadKehilaLog(this.baseUrl, this.authToken, this.mekomeCommunityId)
  }

  @action goBack = () => {
    if (this.step > 0) this.step -= 1
  }

  @action goNext = async () => {
    if (this.step === 0) {
      this.step = 1
      await this.loadCategories()
      return
    }
    if (this.step === 1) {
      this.step = 2
      return
    }
    if (this.step === 2) {
      // Deliberately no status/import call here - the import must not start (and its progress
      // must not even be queried) until the admin explicitly clicks "Start import". Landing on
      // this step should always show a clean "Start import" button, never "Resume".
      this.step = 3
      return
    }
    if (this.step === 3) {
      this.step = 4
      return
    }
    this.close()
  }

  @computed get isNextBlocked(): string | null {
    if (this.step === 0) return this.probed ? null : i18n.t('kehilaMigration:blocked_testConnection')
    if (this.step === 1) return this.liveCategories.length > 0 ? null : i18n.t('kehilaMigration:blocked_keepOne')
    if (this.step === 2) return this.createResult ? null : i18n.t('kehilaMigration:blocked_createFirst')
    if (this.step === 3) {
      if (!this.importStatus) return i18n.t('kehilaMigration:blocked_startImport')
      if (this.importStatus.isRunning) return i18n.t('kehilaMigration:blocked_stillRunning')
      if (this.importStatus.done < this.importStatus.total) return i18n.t('kehilaMigration:blocked_startImport')
      return null
    }
    return null
  }
}

const kehilaMigrationWizardStore = new KehilaMigrationWizardStore()
export default kehilaMigrationWizardStore
